import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FaUserCircle,
  FaEnvelope,
  FaPhone,
  FaIdCard,
  FaSpinner,
  FaSignOutAlt,
  FaArrowLeft,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Header from "./header";
import Footer from "./Footer";
import { Helmet } from "react-helmet-async";

const AUTH_API_URL = process.env.REACT_APP_API_URL;

const ProfilePage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const userId =
      localStorage.getItem("user_id") ||
      JSON.parse(localStorage.getItem("userData") || "{}")?.id;
    fetchProfile(userId);
  }, [navigate]);

  const fetchProfile = async (userId) => {
    try {
      const response = await axios.post(
        `${AUTH_API_URL}/get-profile`,
        { user_id: String(userId) },
        { headers: { "Content-Type": "application/json" } },
      );

      if (response.data?.success && response.data.data?.length > 0) {
        const user = response.data.data[0];
        setProfile({
          ...user,
          name: user.full_name,
        });
      } else {
        setError("Profile not found");
      }
    } catch (err) {
      console.error("Profile API error:", err);
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/auth");
  };

  // ---------- STYLES ----------
  const styles = {
    back: {
      marginTop: "20px",
      fontSize: "18px",
      color: "#0f172a",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      justifyContent: "center",
    },

    container: { maxWidth: "800px", margin: "40px auto", padding: "20px" },
    card: {
      background: "linear-gradient(135deg, #e23552 0%, #0e0f0f 100%)",
      borderRadius: "24px",
      padding: "40px",
      boxShadow: "0 10px 40px -10px rgba(0,0,0,0.08)",
      border: "1px solid #E2E8F0",
      textAlign: "center",
    },
    avatar: {
      width: "100px",
      height: "100px",
      borderRadius: "50%",
      background: "#F1F5F9",
      color: "#94A3B8",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "42px",
      margin: "0 auto 20px",
    },
    title: { fontSize: "24px", fontWeight: "800", color: "#0F172A" },
    subtitle: { fontSize: "14px", color: "#64748B", marginBottom: "30px" },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "20px",
      textAlign: "left",
    },
    infoItem: {
      background: "#F8FAFC",
      padding: "20px",
      borderRadius: "16px",
      border: "1px solid #F1F5F9",
    },
    label: {
      fontSize: "12px",
      color: "#64748B",
      textTransform: "uppercase",
      fontWeight: "700",
      marginBottom: "8px",
    },
    value: {
      fontSize: "16px",
      color: "#0F172A",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    icon: { color: "#D60000" },
    logoutBtn: {
      marginTop: "40px",
      padding: "12px 24px",
      background: "#FEE2E2",
      color: "#B91C1C",
      border: "none",
      borderRadius: "12px",
      fontWeight: "700",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
    },
  };

  if (loading) {
    return (
      <>
        <Header />
        <div style={{ ...styles.container, textAlign: "center" }}>
          <FaSpinner
            className="fa-spin"
            style={{ fontSize: "32px", color: "#D60000" }}
          />
        </div>
      </>
    );
  }
  const seo = {
    title: profile?.name
      ? `${profile.name} | My Profile | CGRMART`
      : "User Profile | CGRMART",
    description:
      "View and manage your CGRMART profile details including name, email, and phone number.",
    keywords:
      "user profile, account details, CGRMART profile, EV store account",
  };
  <Helmet>
    <title>{seo.title}</title>

    <meta name="description" content={seo.description} />
    <meta name="keywords" content={seo.keywords} />

    {/* Open Graph */}
    <meta property="og:title" content={seo.title} />
    <meta property="og:description" content={seo.description} />
    <meta property="og:type" content="website" />
    <meta property="og:url" content={window.location.href} />

    {/* Twitter */}
    <meta name="twitter:title" content={seo.title} />
    <meta name="twitter:description" content={seo.description} />

    {/* Canonical */}
    <link rel="canonical" href={window.location.href} />
    
  </Helmet>;
  return (
    <div style={{ backgroundColor: "#F8FAFC", minHeight: "100vh" }}>
      <Header />
      <style>{`
        @media (max-width: 768px) {
          .profile-container { max-width: 100% !important; padding: 20px 15px !important; }
          .profile-card { padding: 30px 20px !important; }
          .profile-avatar { width: 80px !important; height: 80px !important; font-size: 36px !important; }
          .profile-title { font-size: 20px !important; }
          .profile-grid { grid-template-columns: 1fr !important; gap: 15px !important; }
        }
        @media (max-width: 480px) {
          .profile-container { padding: 15px 10px !important; }
          .profile-card { padding: 25px 15px !important; }
          .profile-avatar { width: 70px !important; height: 70px !important; font-size: 32px !important; }
          .profile-title { font-size: 18px !important; }
        }
      `}</style>
      <div style={styles.container} className="profile-container">
        <div style={styles.back} onClick={() => navigate(-1)}>
          <FaArrowLeft /> <b>Back</b>
        </div>
        <br></br>

        {error ? (
          <div
            style={{ color: "#EF4444", fontWeight: "600", textAlign: "center" }}
          >
            {error}
          </div>
        ) : (
          <div style={styles.card} className="profile-card">
            <div style={styles.avatar} className="profile-avatar">
              {profile?.name ? (
                profile.name.charAt(0).toUpperCase()
              ) : (
                <FaUserCircle />
              )}
            </div>

            <h1 style={styles.title} className="profile-title">
              {profile?.name}
            </h1>
            <p style={styles.subtitle}>Manage your account details</p>

            <div style={styles.grid} className="profile-grid">
              <div style={styles.infoItem}>
                <div style={styles.label}>Full Name</div>
                <div style={styles.value}>
                  <FaIdCard style={styles.icon} /> {profile?.name}
                </div>
              </div>

              <div style={styles.infoItem}>
                <div style={styles.label}>Email</div>
                <div style={styles.value}>
                  <FaEnvelope style={styles.icon} /> {profile?.email || "N/A"}
                </div>
              </div>

              <div style={styles.infoItem}>
                <div style={styles.label}>Phone</div>
                <div style={styles.value}>
                  <FaPhone style={styles.icon} /> {profile?.mobile || "N/A"}
                </div>
              </div>

              <div style={styles.infoItem}>
                <div style={styles.label}>User ID</div>
                <div style={styles.value}>
                  #{profile?.user_id || profile?.id}
                </div>
              </div>
            </div>

            <button style={styles.logoutBtn} onClick={handleLogout}>
              <FaSignOutAlt /> Sign Out
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProfilePage;
